import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const extinguisherTypes = [
  {
    id: "abc-dry-powder",
    code: "ABC",
    colorClass: "bg-primary-100 text-primary-700 border-primary-200",
    badgeClass: "bg-primary-500 text-background-50",
    title: "ABC Dry Powder Fire Extinguishers",
    fireClasses: "Class A, B and C fires",
    fuelSources: "Flammable solids, liquids and gas",
    agent: "Specialised chemical dry powder stored in pressurized cylinders",
    description:
      "The fire suppression agent in these fire extinguishers is a specialised chemical dry powder, which is designed for putting out Class A, B and C fires. The agent is stored in pressurized cylinders with nozzles to discharge it to the fire upon release. The fire extinguisher can extinguish fires caused by flammable solids, liquids and gas. These are among the most versatile fire extinguishers available.",
    icon: "ri-shield-check-line",
  },
  {
    id: "co2",
    code: "CO₂",
    colorClass: "bg-accent-100 text-accent-700 border-accent-200",
    badgeClass: "bg-accent-500 text-background-50",
    title: "CO₂ Fire Extinguishers",
    fireClasses: "Electrical fires, combustible liquids and gases",
    fuelSources: "Electrical equipment, flammable liquids and gases",
    agent: "Pressurized carbon dioxide gas",
    description:
      "These fire extinguishers contain pressurized carbon dioxide gas used to put out electrical fires. It can also work on putting out combustible liquids and gases. The key property of this fire suppression agent is that it rapidly sucks out oxygen from flames, thereby stopping the fires quickly and safely. CO2 extinguishers leave no residue and are ideal where clean-up is important.",
    icon: "ri-cloud-line",
  },
  {
    id: "wet-chemical",
    code: "F",
    colorClass: "bg-secondary-100 text-secondary-700 border-secondary-200",
    badgeClass: "bg-secondary-500 text-background-50",
    title: "Wet Chemical Fire Extinguishers",
    fireClasses: "Class F fires, Class A fires",
    fuelSources: "Cooking oils, fat and grease; burning clothes and paper",
    agent: "Wet chemical solution that creates a soapy seal",
    description:
      "Wet chemical fire extinguishers are specifically made for fighting Class F fires that result from cooking oils, fat and grease commonly found in kitchens and restaurants. The extinguishers can also be effective on Class A fires resulting from burning clothes and paper materials. The fire suppression agent in the cylinder fights the flames by eliminating the heat element, cooling the oils and creating a soapy solution that seals off the surfaces. This not only stops the fires but also prevents them from re-ignition.",
    icon: "ri-restaurant-2-line",
  },
  {
    id: "class-d",
    code: "D",
    colorClass: "bg-background-200 text-foreground-700 border-background-300",
    badgeClass: "bg-foreground-700 text-background-50",
    title: "Class D Fire Extinguishers",
    fireClasses: "Class D fires (combustible metals)",
    fuelSources: "Magnesium, titanium, potassium, aluminium and other combustible metals",
    agent: "Sand, metals or a combination",
    description:
      "These are not very common since they are mainly used in industrial premises to stop fires caused by flammable metals. The fire suppression agent in the cylinders comprises of sand, metals or both, which is effective in extinguishing metals on fire. Class D extinguishers are primarily found in laboratories and industrial manufacturing facilities.",
    icon: "ri-settings-3-line",
  },
  {
    id: "foam",
    code: "AB",
    colorClass: "bg-accent-50 text-accent-600 border-accent-100",
    badgeClass: "bg-accent-400 text-background-50",
    title: "Foam Fire Extinguishers",
    fireClasses: "Class A and B fires",
    fuelSources: "Flammable liquids and gases",
    agent: "Foam that removes oxygen from the chemical reaction",
    description:
      "These fire extinguishers are filled up with foam as the fire suppression agent. Foam fire extinguishers are designed for Class A and B fires resulting from flammable liquids and gases. The agent works by removing oxygen from the chemical reaction, hence stopping the flames. Foam fire extinguishers are usually very messy on electrical fires and should never be used in such situations. Besides, the chemical composition of the foam can also cause damage to your electrical systems.",
    icon: "ri-bubble-chart-line",
  },
  {
    id: "water",
    code: "A",
    colorClass: "bg-primary-50 text-primary-600 border-primary-100",
    badgeClass: "bg-primary-400 text-background-50",
    title: "Water-Based Fire Extinguishers",
    fireClasses: "Class A fires (ordinary combustibles)",
    fuelSources: "Flammable solids like clothes, paper, wood and plastics",
    agent: "Pressurized water",
    description:
      "These are fire extinguishers that use pressurized water as the primary fire suppression agent. Water-based fire extinguishers are made for putting out Class fires that result from flammable solids like clothes, paper materials, wood and plastics. Water mainly works by eliminating the heat element from the flames, which cools off the burning materials and stops the fire. They should not be used on electrical fires or flammable liquid fires.",
    icon: "ri-drop-line",
  },
];

export default function TypesOfFireExtinguishersPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: extinguisherTypes.map((et) => ({
              "@type": "Question",
              name: `What are ${et.title}?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `${et.description} Suitable for: ${et.fireClasses}.`,
              },
            })),
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/97b59a0f-efdc-40c1-b4a1-5ade0b70d15c_compressed_Depositphotos_40039179_l-2015.webp"
              alt="Types of Fire Extinguishers Singapore"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Types of Fire Extinguishers</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Equipment Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Types of Fire Extinguishers <span className="text-primary-400">in Singapore</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Fire extinguishers are critical fire protection systems recommended for all residential, commercial and industrial buildings. Learn which type is right for your premises.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Why They Matter
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why You Need the Right Fire Extinguisher
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Fire extinguishers serve a wide range of applications when it comes to fire safety. Apart from saving lives, fire extinguishers can also enable you to significantly reduce the destruction to your property during fires. With efficient fire extinguishers, you can easily and safely put out small fires even before the arrival of fire rescue services.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                In Singapore, there are strict fire safety regulations that stipulate the types of fire extinguishers that should be installed in residential, commercial and industrial premises. Depending on the potential fire threats on your building, there are various kinds of fire extinguishers to choose from.
              </p>
              <div className="bg-primary-500 rounded-2xl p-6 text-background-50 flex flex-col justify-between mt-6">
                <div>
                  <i className="ri-shield-star-line text-3xl mb-3 block"></i>
                  <h3 className="font-heading text-lg font-bold mb-2">Quick Navigation</h3>
                  <ul className="space-y-2">
                    {extinguisherTypes.map((et) => (
                      <li key={et.id}>
                        <a
                          href={`#${et.id}`}
                          className="flex items-center gap-2 text-sm text-background-100 hover:text-background-50 transition-colors"
                        >
                          <i className="ri-arrow-right-s-line text-primary-300"></i>
                          {et.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/f1b898d6-3f85-46f1-8b2c-c8abec46cc38_compressed_Depositphotos_63936807_l-2015-min.webp"
                alt="Red coiled fire hose with metal connectors and nozzle"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Extinguisher Types */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                The Six Types
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Main Types of Fire Extinguishers <span className="text-primary-500">in Singapore</span>
              </h2>
            </div>
            <div className="flex flex-col gap-6">
              {extinguisherTypes.map((et) => (
                <div key={et.id} id={et.id} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="flex flex-col md:flex-row md:items-start gap-5">
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-heading font-bold border-2 ${et.colorClass}`}>
                        {et.code}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                        <a href={`#${et.id}`}>{et.title}</a>
                      </h3>
                      <p className="text-foreground-600 text-base leading-relaxed mb-4">{et.description}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-background-100 rounded-xl p-3 border border-background-200/70">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-1">Fire Classes</p>
                          <p className="text-foreground-800 text-sm">{et.fireClasses}</p>
                        </div>
                        <div className="bg-background-100 rounded-xl p-3 border border-background-200/70">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-1">Fuel Sources</p>
                          <p className="text-foreground-800 text-sm">{et.fuelSources}</p>
                        </div>
                        <div className="bg-primary-50 rounded-xl p-3 border border-primary-100">
                          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Suppression Agent</p>
                          <p className="text-foreground-800 text-sm">{et.agent}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="bg-primary-500 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                Find the Right Fire Extinguisher for Your Building
              </h2>
              <p className="text-background-100 text-sm md:text-base max-w-xl">
                Ezzogenics offers a huge selection of all types of fire extinguishers. Based on the potential fire risks on your premises and your budget, we can help you choose the most suitable fire extinguishers for better fire safety. We work with leading brands, offering high quality and industry-standard fire extinguishers.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Browse Fire Extinguishers
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Get Expert Advice
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}